// src/lib/roles.ts
export const ROLES = {
  VIEWER: 1,
  FIELD_SERVICE_ENGINEER: 2,

  // legacy alias, keep for compatibility
  STAFF: 2,

  // legacy manager code, normalize to ADMIN access
  MANAGER: 3,

  ADMIN: 4,

  // legacy alias, keep for compatibility
  ADMIN_ASSISTANT: 4,

  OWNER: 1024,
} as const;

export type RoleCode = (typeof ROLES)[keyof typeof ROLES];

export const APP_MODULES = [
  {
    key: "dashboard",
    label: "Dashboard",
    description: "Overview, analytics, and quick system status.",
  },
  {
    key: "service_reports",
    label: "Service Reports",
    description: "Create, edit, submit, sign, and review service reports.",
  },
  {
    key: "service_report_cms",
    label: "Service Report CMS",
    description: "Manage templates, workflows, and report-related admin setup.",
  },
  {
    key: "pms_scheduler",
    label: "PMS Scheduler",
    description: "Schedule, assign, filter, and manage PMS activities.",
  },
  {
    key: "pms_checklist_cms",
    label: "PMS Checklist CMS",
    description: "Manage checklist templates and attached checklist flows.",
  },
  {
    key: "equipment_registry",
    label: "Equipment Registry",
    description: "Manage equipment, clinic, serial, and related records.",
  },
  {
    key: "parts_inventory",
    label: "Parts Inventory",
    description: "View and manage parts stock, usage, and inventory records.",
  },
  {
    key: "settings",
    label: "Settings",
    description: "Branding, email templates, and system-wide settings.",
  },
  {
    key: "audit_log",
    label: "Audit Log",
    description: "Review system activity and important record changes.",
  },
  {
    key: "users",
    label: "User Management",
    description: "Create accounts, assign roles, and manage access.",
  },
] as const;

export type AppModule = (typeof APP_MODULES)[number]["key"];
export type PermissionAction = "access" | "edit";

export type ModulePermissionState = {
  access: boolean;
  edit: boolean;
};

export type ModulePermissionMatrix = Record<AppModule, ModulePermissionState>;

const APP_MODULE_KEYS = APP_MODULES.map((m) => m.key) as AppModule[];

export function normalizeRoleCode(roleCode: number | string | undefined | null) {
  const n = Number(roleCode || 0);

  // migrate old Manager behavior into Admin behavior
  if (n === ROLES.MANAGER) return ROLES.ADMIN;

  return n;
}

export const ROLE_LABELS: Record<number, string> = {
  [ROLES.VIEWER]: "Viewer",
  [ROLES.FIELD_SERVICE_ENGINEER]: "Field Service Engineer",
  [ROLES.ADMIN]: "Admin",
  [ROLES.OWNER]: "Owner",
  [ROLES.MANAGER]: "Admin",
};

export const ROLE_DESCRIPTIONS: Record<number, string> = {
  [ROLES.VIEWER]:
    "Can view available modules but cannot create, edit, submit, delete, or change system data.",
  [ROLES.FIELD_SERVICE_ENGINEER]:
    "Can work on Service Reports, PMS Scheduler, and PMS Checklist CMS.",
  [ROLES.ADMIN]:
    "Can manage Equipment Registry, PMS Checklist CMS, PMS Scheduler, Settings, Audit Log, and Service Report CMS.",
  [ROLES.OWNER]:
    "Full access to all modules and management features.",
  [ROLES.MANAGER]: "Admin",
};

export function roleLabel(roleCode: number) {
  return ROLE_LABELS[normalizeRoleCode(roleCode)] || `Role ${roleCode}`;
}

export function createEmptyPermissionMatrix(): ModulePermissionMatrix {
  return APP_MODULES.reduce((acc, mod) => {
    acc[mod.key] = { access: false, edit: false };
    return acc;
  }, {} as ModulePermissionMatrix);
}

function setModule(
  matrix: ModulePermissionMatrix,
  module: AppModule,
  access: boolean,
  edit: boolean,
) {
  matrix[module] = {
    access,
    edit: access ? edit : false,
  };
}

export function getDefaultPermissionMatrixForRole(
  roleCode: number,
): ModulePermissionMatrix {
  const role = normalizeRoleCode(roleCode);
  const matrix = createEmptyPermissionMatrix();

  if (role === ROLES.OWNER) {
    APP_MODULE_KEYS.forEach((module) => setModule(matrix, module, true, true));
    return matrix;
  }

  if (role === ROLES.ADMIN) {
    setModule(matrix, "dashboard", true, false);
    setModule(matrix, "service_reports", true, true);
    setModule(matrix, "service_report_cms", true, true);
    setModule(matrix, "pms_scheduler", true, true);
    setModule(matrix, "pms_checklist_cms", true, true);
    setModule(matrix, "equipment_registry", true, true);
    setModule(matrix, "parts_inventory", false, false);
    setModule(matrix, "settings", true, true);
    setModule(matrix, "audit_log", true, true);
    setModule(matrix, "users", false, false);
    return matrix;
  }

  if (role === ROLES.FIELD_SERVICE_ENGINEER) {
    setModule(matrix, "dashboard", true, false);
    setModule(matrix, "service_reports", true, true);
    setModule(matrix, "service_report_cms", false, false);
    setModule(matrix, "pms_scheduler", true, true);
    setModule(matrix, "pms_checklist_cms", true, true);
    setModule(matrix, "equipment_registry", false, false);
    setModule(matrix, "parts_inventory", false, false);
    setModule(matrix, "settings", false, false);
    setModule(matrix, "audit_log", false, false);
    setModule(matrix, "users", false, false);
    return matrix;
  }

  // Viewer: view all available app pages, no edits
  setModule(matrix, "dashboard", true, false);
  setModule(matrix, "service_reports", true, false);
  setModule(matrix, "service_report_cms", true, false);
  setModule(matrix, "pms_scheduler", true, false);
  setModule(matrix, "pms_checklist_cms", true, false);
  setModule(matrix, "equipment_registry", true, false);
  setModule(matrix, "parts_inventory", true, false);
  setModule(matrix, "settings", true, false);
  setModule(matrix, "audit_log", true, false);
  setModule(matrix, "users", false, false);

  return matrix;
}

export function permissionKey(module: AppModule, action: PermissionAction) {
  return `${module}:${action}`;
}

export function permissionsToStrings(
  matrix: ModulePermissionMatrix,
): string[] {
  const out: string[] = [];

  APP_MODULE_KEYS.forEach((module) => {
    if (matrix[module].access) out.push(permissionKey(module, "access"));
    if (matrix[module].edit) out.push(permissionKey(module, "edit"));
  });

  return out;
}

export function permissionStringsToMatrix(
  permissions: string[] = [],
): ModulePermissionMatrix {
  const matrix = createEmptyPermissionMatrix();

  permissions.forEach((entry) => {
    const [rawModule, rawAction] = String(entry || "").split(":");

    if (
      APP_MODULE_KEYS.includes(rawModule as AppModule) &&
      (rawAction === "access" || rawAction === "edit")
    ) {
      const module = rawModule as AppModule;
      const action = rawAction as PermissionAction;

      if (action === "access") {
        matrix[module].access = true;
      }

      if (action === "edit") {
        matrix[module].access = true;
        matrix[module].edit = true;
      }
    }
  });

  return matrix;
}

export function resolvePermissionMatrix(
  roleCode: number,
  permissions: string[] = [],
): ModulePermissionMatrix {
  if (Array.isArray(permissions) && permissions.length > 0) {
    return permissionStringsToMatrix(permissions);
  }

  return getDefaultPermissionMatrixForRole(roleCode);
}

export function canAccessModule(
  roleCode: number,
  permissions: string[] = [],
  module: AppModule,
) {
  const matrix = resolvePermissionMatrix(roleCode, permissions);
  return !!matrix[module]?.access;
}

export function canEditModule(
  roleCode: number,
  permissions: string[] = [],
  module: AppModule,
) {
  const matrix = resolvePermissionMatrix(roleCode, permissions);
  return !!matrix[module]?.edit;
}

export function canManageUsers(roleCode: number, permissions: string[] = []) {
  return canAccessModule(roleCode, permissions, "users");
}